import "@/components/BrandTheme.css";
import React from "react";
import ProfileEditor from "@/components/ProfileEditor";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut, LayoutDashboard, MessageSquare, Users, Inbox, Shield, Wifi, WifiOff, AlertTriangle, QrCode } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useWhatsAppStatus } from "@/hooks/useWhatsAppStatus";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { NAV, HEADER } from "@/constants/testIds";

const linkBase = "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200";

function StatusBadge({ status }) {
  const map = {
    connected:   { icon: Wifi,          label: "Conectado",       cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" },
    qr_pending:  { icon: QrCode,        label: "Aguardando QR",   cls: "bg-amber-500/10 text-amber-400 border-amber-500/30" },
    disconnected:{ icon: WifiOff,       label: "Desconectado",    cls: "bg-slate-500/10 text-slate-400 border-slate-500/30" },
    banned:      { icon: AlertTriangle, label: "Banido",          cls: "bg-rose-500/10 text-rose-400 border-rose-500/30" },
  };
  const s = map[status] || map.disconnected;
  const Icon = s.icon;
  return (
    <Badge variant="outline" data-testid={HEADER.connectionIndicator} className={`gap-1.5 px-2.5 py-1 ${s.cls}`}>
      <Icon className="w-3.5 h-3.5" />
      <span className="text-xs font-mono uppercase tracking-wider">{s.label}</span>
    </Badge>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const [profileOpen, setProfileOpen] = React.useState(false);
  const loc = useLocation();
  const nav = useNavigate();
  const { status } = useWhatsAppStatus();

  const items = [
    { to: "/", label: "DI\u00c1RIA", icon: LayoutDashboard, tid: NAV.dashboard },
    { to: "/whatsapp", label: "QR Code", icon: MessageSquare, tid: NAV.myWhatsapp },
    { to: "/contacts", label: "Conversas e funil", icon: Users, tid: NAV.contacts },
    { to: "/leads", label: "Leads", icon: Inbox, tid: NAV.feedbacks },
  ];
  if (user && (user.role === "supervisor" || user.role === "admin")) {
    items.push({ to: "/supervisor", label: "Supervisor", icon: Shield, tid: NAV.supervisor });
  }

  return (
    <div className="help-brand-theme min-h-screen bg-[#0B0F17] text-slate-100 grain relative">
      {profileOpen && <ProfileEditor onClose={() => setProfileOpen(false)} />}
      <aside className="hidden lg:flex fixed left-0 top-0 bottom-0 w-64 bg-[#0F172A] border-r border-slate-800/80 flex-col p-4 z-40">
        <div className="mb-8 px-2">
          <div className="flex items-center gap-2">
            <img src="/gatinho.png" alt="Gatinho Acompanhamento Vendas" className="w-10 h-10 shrink-0 object-contain" />
            <div>
              <div className="font-bold text-lg leading-tight">Acompanhamento Vendas</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">ACOMPANHAMENTO DE VENDAS</div>
            </div>
          </div>
        </div>
        <nav className="flex flex-col gap-1 flex-1">
          {items.map((it) => {
            const active = loc.pathname === it.to || (it.to !== "/" && loc.pathname.startsWith(it.to));
            const Icon = it.icon;
            return (
              <Link key={it.to} to={it.to} data-testid={it.tid}
                className={`${linkBase} ${active ? "bg-pink-500/20 text-pink-300 border border-pink-500/30" : "text-slate-400 hover:bg-slate-800/50 hover:text-slate-100"}`}>
                <Icon className="w-4 h-4" />
                {it.label}
              </Link>
            );
          })}
        </nav>
        <div className="pt-4 border-t border-slate-800">
          <button type="button" onClick={() => setProfileOpen(true)} aria-label="Editar meu perfil" className="flex w-full items-center gap-3 rounded-lg px-2 py-2 mb-3 text-left hover:bg-slate-800">
            {user?.profile_photo ? <img src={user.profile_photo} alt="" className="h-10 w-10 shrink-0 rounded-full object-cover" /> : <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-pink-500/20 text-pink-300">{(user?.name || user?.email || "?").slice(0,1).toUpperCase()}</div>}
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{user?.name || user?.email}</div>
              <div className="text-xs text-pink-300">Meu perfil</div>
            </div>
          </button>
          <Button variant="outline" size="sm" className="w-full justify-start border-slate-800 hover:bg-slate-800/60"
                  onClick={async () => { await logout(); nav("/login"); }} data-testid={HEADER.logoutButton}>
            <LogOut className="w-4 h-4 mr-2" /> Sair
          </Button>
        </div>
      </aside>

      <header className="lg:pl-64 sticky top-0 z-30 backdrop-blur-xl bg-[#0B0F17]/80 border-b border-slate-800/80">
        <div className="flex items-center justify-between px-6 py-3.5">
          <div className="flex items-center gap-3">
            <h1 className="text-lg font-semibold hidden md:block">
              {items.find(i => loc.pathname === i.to || (i.to !== "/" && loc.pathname.startsWith(i.to)))?.label || "DI\u00c1RIA"}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => setProfileOpen(true)} className="lg:hidden text-xs text-pink-300">Meu perfil</button>
            <StatusBadge status={status} />
          </div>
        </div>
      </header>

      <main className="lg:pl-64">
        <div className="p-6 md:p-8 max-w-7xl mx-auto">
          {children}
        </div>
      </main>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-[#0F172A] border-t border-slate-800/80 flex justify-around p-2">
        {items.slice(0, 5).map((it) => {
          const active = loc.pathname === it.to || (it.to !== "/" && loc.pathname.startsWith(it.to));
          const Icon = it.icon;
          return (
            <Link key={it.to} to={it.to} className={`flex flex-col items-center gap-1 px-3 py-1.5 rounded-lg text-[10px] ${active ? "text-emerald-400" : "text-slate-500"}`}>
              <Icon className="w-4 h-4" />
              {it.label.split(" ")[0]}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}




